import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getAllChats,
  getSingleChat,
  generate,
  createChat,
  deleteChat,
  completions,
  getAllAbilities,
  updateChat,
} from "../Api/chat";
import {
  ChatHistory,
  SingleChat,
  Generate,
  ChatData,
  CompletionsRequest,
  CompletionsResponse,
  Ability,
  ChatUpdatePayload,
} from "../Models/chatUI";

export const useChats = () => {
  return useQuery<ChatHistory[], Error>({
    queryKey: ["chats"],
    queryFn: () => getAllChats(),
  });
};

export const useUpdateChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { chatId: string; data: ChatUpdatePayload }) =>
      updateChat(variables.chatId, variables.data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats"] });
    },
  });
};

export const useSingleChat = (chatId: string) => {
  return useQuery<SingleChat, Error>({
    queryKey: ["singleChat", chatId],
    queryFn: () => getSingleChat(chatId),
    enabled: !!chatId,
  });
};

export const useGenerateMessage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: { chatId: string; message: Generate }) =>
      generate(variables.chatId, variables.message),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["singleChat", variables.chatId],
      });
    },
  });
};

export const useCreateNewChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newChatData: ChatData) => createChat(newChatData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats"] });
    },
  });
};

export const useDeleteChat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: string) => deleteChat(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chats"] });
    },
  });
};

export const useCompletions = () => {
  return useMutation<CompletionsResponse, Error, CompletionsRequest>({
    mutationFn: (request: CompletionsRequest) => completions(request),
  });
};

export const useChatAbilities = () => {
  return useQuery<Ability[]>({
    queryKey: ["chatAbilities"],
    queryFn: () => getAllAbilities(),
  });
};
